import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { gsap, motionOK, scrollToId } from "../hooks/useSmoothScroll";
import { useLang } from "../hooks/useLanguage";

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { t, dir } = useLang();

  useEffect(() => {
    if (!motionOK()) return undefined;
    const ctx = gsap.context(() => {
      gsap.to(".hero-bg", {
        yPercent: 16,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top top", end: "bottom top", scrub: true },
      });
      gsap.to(".hero-fg", {
        yPercent: -14,
        opacity: 0.12,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top top", end: "82% top", scrub: true },
      });
      gsap.to(".hero-veil", {
        opacity: 0.92,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top top", end: "bottom top", scrub: true },
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  const line = (delay) => ({
    initial: reduce ? false : { y: "115%" },
    animate: { y: "0%" },
    transition: { duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden bg-[#141414]" data-testid="hero-section">
      <div className="hero-bg absolute inset-0">
        <img
          src={t.IMAGES.hero}
          alt={t.UI.heroImageAlt}
          className="h-full w-full object-cover scale-[1.15]"
          fetchPriority="high"
        />
      </div>
      <div className="hero-veil absolute inset-0 bg-gradient-to-b from-[#141414]/80 via-[#141414]/60 to-[#141414] opacity-70" />
      <div
        className={`absolute inset-0 ${
          dir === "rtl"
            ? "bg-gradient-to-l from-[#141414]/85 via-[#141414]/35 to-transparent"
            : "bg-gradient-to-r from-[#141414]/85 via-[#141414]/35 to-transparent"
        }`}
      />

      <div className="hero-fg relative z-10 max-w-7xl mx-auto px-6 sm:px-10 flex flex-col justify-center min-h-[100svh] pt-24 pb-[21rem] sm:pb-64">
        <motion.p
          className="label-caps mb-8"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          data-testid="hero-eyebrow"
        >
          {t.UI.heroEyebrow}
        </motion.p>
        <h1 className="font-serif-lux font-light text-ivory text-[clamp(2.9rem,8.5vw,6.5rem)] leading-[1.02] tracking-tight max-w-5xl">
          <span className="mask-line">
            <motion.span {...line(0.35)}>{t.UI.heroTitleA}</motion.span>
          </span>
          <span className="mask-line">
            <motion.span {...line(0.5)}>
              {t.UI.heroTitleB1 && <>{t.UI.heroTitleB1} </>}
              <em className="not-italic text-champagne">{t.UI.heroTitleEm}</em>
            </motion.span>
          </span>
        </h1>
        <motion.p
          className="mt-8 font-serif-lux italic text-xl sm:text-2xl text-ivory/85 max-w-2xl"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.85 }}
          data-testid="hero-subheadline"
        >
          {t.UI.heroSub}
        </motion.p>
        <motion.div
          className="mt-6 text-sm text-ivory/60 max-w-xl leading-relaxed space-y-3"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
        >
          {t.UI.heroBody.map((p) => (
            <p key={p.slice(0, 20)}>{p}</p>
          ))}
        </motion.div>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
        >
          <button className="btn-lux btn-lux-solid" onClick={() => scrollToId("consultation")} data-testid="hero-cta-consultation">
            {t.UI.heroCtaPrimary}
          </button>
          <button className="btn-lux btn-lux-outline" onClick={() => scrollToId("how-it-works")} data-testid="hero-cta-how">
            {t.UI.heroCtaSecondary}
          </button>
        </motion.div>
      </div>

      <div className="hero-stats absolute bottom-0 inset-x-0 z-10 border-t hairline-dark bg-[#141414]/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 sm:grid-cols-3">
          {t.HERO_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              className={`py-6 sm:py-8 ${i > 0 ? "border-t sm:border-t-0 sm:border-s hairline-dark sm:ps-10" : ""}`}
              initial={reduce ? false : { opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 + i * 0.14 }}
              data-testid={`hero-stat-${i + 1}`}
            >
              <div className="font-serif-lux text-3xl sm:text-4xl text-champagne font-light">{s.value}</div>
              <div className="mt-1 text-[0.68rem] uppercase tracking-[0.22em] text-ivory/60">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
